<template>
  <DynamicActionsMenu
    :actions="actions"
    :options="menuOptions"
    @action="handleAction"
  >
    <template #trigger>
      <slot name="trigger">
        <EllipsisIcon class="tooltip-trigger" width="20" />
      </slot>
    </template>
    <template #footer>
      <slot name="footer" />
    </template>
  </DynamicActionsMenu>
</template>

<script setup lang="ts">
import { computed } from "vue";

import DynamicActionsMenu from "@/components/DynamicActionsMenu.vue";
import { useProgressBillingLabels } from "@/composables/useProgressBillingLabels";
import EllipsisIcon from "@/images/dots-horizontal-outline.svg?component";
import type { Invoice } from "@/typings/invoice";
import type { InvoiceMenuAction, MenuAction, TippyOptions } from "@/typings/ui";
import { InvoiceAction } from "@/typings/ui";

const { programName } = useProgressBillingLabels();

const props = defineProps<{
  invoice: Invoice;
  tippyOptions?: TippyOptions;
  visibleActions?: InvoiceAction[];
  // True when the tenant has Progress Billing turned on in settings.
  // Lifted from the parent ContextMenu (which fetches it lazily) so the
  // "Configure Payment Schedule" entry only appears when the tenant
  // actually permits SPB.
  tenantSpbEnabled?: boolean;
  // True when the tenant has Pay by SMS enabled. Drives "Send Pay Link"
  // visibility: the action shows whenever the feature is on and is only
  // *disabled* (not hidden) when a send isn't possible for this invoice
  // (e.g. fully paid). It's hidden only when the feature is off.
  payBySmsEnabled?: boolean;
  // True when the tenant has Pay by Email enabled. Same visibility model as
  // payBySmsEnabled, for the "Send Pay Link by Email" action (TB-2027).
  payByEmailEnabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "invoice-action", payload: { id: number; action: InvoiceAction }): void;
}>();

const menuOptions = computed<TippyOptions>(() => ({
  theme: "light",
  appendTo: document.body,
  ...props.tippyOptions
}));

const fullyPaid = computed<boolean>(() => {
  const inv = props.invoice;
  const spb = inv.progress_billing;
  if (spb?.is_progress_billing) {
    return (spb.total_balance_remaining_cents ?? 0) <= 0;
  }
  return (inv.financials?.balance_cents ?? 0) <= 0;
});

// Any pay-link channel enabled — gates the single Send/Revoke Pay Link entries.
// Both labels name the single enabled channel when only one is on ("…by SMS"/
// "…by Email", "Revoke SMS/Email Pay Link"); with both on they're generic
// ("Send Pay Link", "Revoke Pay Link") and the modal offers the channel choice.
const payLinkLabel = computed<string>(() => {
  if (props.payBySmsEnabled && props.payByEmailEnabled) return "Send Pay Link";
  if (props.payByEmailEnabled) return "Send Pay Link by Email";
  return "Send Pay Link by SMS";
});

const revokePayLinkLabel = computed<string>(() => {
  if (props.payBySmsEnabled && props.payByEmailEnabled) return "Revoke Pay Link";
  if (props.payByEmailEnabled) return "Revoke Email Pay Link";
  return "Revoke SMS Pay Link";
});

const payLinkFeatureOn = computed<boolean>(
  () => Boolean(props.payBySmsEnabled) || Boolean(props.payByEmailEnabled)
);

const spbActive = computed<boolean>(
  () => props.invoice?.progress_billing?.is_progress_billing === true
);
const spbCancelled = computed<boolean>(() =>
  Boolean((props.invoice?.progress_billing as any)?.cancelled)
);
const spbLocked = computed<boolean>(() => Boolean(props.invoice?.finalized));

const allActions = computed<InvoiceMenuAction[]>(() => {
  const inv = props.invoice;
  const links = inv.links || ({} as NonNullable<Invoice["links"]>);

  // Groups order the menu visually. Adjacent items with different groups
  // get a 1px divider; ActionsMenu doesn't print section labels because
  // the headers read as noise when only some groups have them. Order is
  // workflow-driven: status changes first, then payment ops (incl. SPB
  // schedule edits next to refund/resubmit), then content edits, then
  // delivery/accounting.
  const base: InvoiceMenuAction[] = [
    {
      key: InvoiceAction.Approve,
      label: "Approve Invoice",
      icon: "fa-regular fa-thumbs-up",
      eventName: "approve-invoice",
      className: "menu-item",
      link: links.invoice_approve,
      group: "status"
    },
    {
      key: InvoiceAction.Unapprove,
      label: "Edit Invoice",
      icon: "far fa-pen-to-square",
      eventName: "unapprove-invoice",
      className: "menu-item",
      link: links.invoice_break,
      group: "status"
    },
    {
      key: InvoiceAction.Void,
      label: "Void Invoice",
      icon: "fa-regular fa-ban",
      eventName: "void-invoice",
      className: "menu-item",
      link: links.void,
      group: "status"
    },
    {
      key: InvoiceAction.AcceptPayment,
      label: "Accept Payment",
      icon: "fa-regular fa-credit-card",
      eventName: "accept-payment",
      className: "menu-item",
      link: links.payment_create,
      disabled: !links.payment_create || fullyPaid.value,
      group: "payment"
    },
    {
      key: InvoiceAction.IssueRefund,
      label: "Issue Refund",
      icon: "fa-regular fa-money-check-edit-alt",
      eventName: "issue-refund",
      className: "menu-item",
      link: links.invoice_refund,
      group: "payment"
    },
    {
      // One entry for all channels (TB-2027). Visible when any pay-link channel
      // is on; the SMS/Email choice lives in the modal so the menu never grows a
      // row per channel. Disabled when no channel can send for this invoice —
      // the server omits both links.payby and links.payby_email (fully paid /
      // no processor). `visible` (not a missing link) keeps it absent, not
      // greyed, when the feature is off entirely.
      key: InvoiceAction.SendPayLink,
      label: payLinkLabel.value,
      // Discovery showed staff defaulting to manual card entry because they
      // never noticed this option. The pill needs no gate of its own: it
      // rides `visible` below, so it appears exactly when the option does.
      badgeLabel: "New",
      icon: "fa-regular fa-comment-dollar",
      eventName: "send-pay-link",
      // The extra class is the Pendo Feature's binding target (TB-2031). It has
      // to be a stable selector: ActionsMenu puts the same
      // data-cy="actions-menu-action" on every row, and this label changes with
      // which channels the tenant has on ("Send Pay Link" / "…by SMS" /
      // "…by Email"), so a text-matched rule would undercount per tenant.
      className: "menu-item send-pay-link-action",
      link: links.payby || links.payby_email,
      visible: payLinkFeatureOn.value,
      disabled: !links.payby && !links.payby_email,
      tooltip:
        !links.payby && !links.payby_email
          ? fullyPaid.value
            ? "This invoice is fully paid."
            : "Pay by link isn't available for this invoice."
          : "",
      group: "payment"
    },
    {
      // Revoke is channel-agnostic — it retires whichever link is live (the
      // single-live-link invariant), so it's gated on any pay-link channel, not
      // SMS specifically. The server includes links.revoke_pay_link only while a
      // link is live, so the entry is disabled when there's nothing to revoke.
      key: InvoiceAction.RevokePayLink,
      label: revokePayLinkLabel.value,
      icon: "fa-regular fa-comment-slash",
      eventName: "revoke-pay-link",
      className: "menu-item",
      link: links.revoke_pay_link,
      visible: payLinkFeatureOn.value,
      disabled: !links.revoke_pay_link,
      tooltip: !links.revoke_pay_link ? "No active pay link to revoke." : "",
      group: "payment"
    },
    // SPB plan management. Single entry whose label/visibility depends
    // on the invoice's current SPB state:
    //   - SPB on, unlocked     → "Edit Payment Schedule"
    //   - SPB on, locked/cancel → "View Payment Schedule"
    //   - SPB off, tenant on    → "Add Payment Schedule"
    //   - SPB off, tenant off / no job → hidden
    // Handler in InvoiceActionsContextMenu opens the same modal in all
    // cases; the modal renders the create vs. edit form based on the
    // fetched plan. The entry carries no link, so an explicit `disabled`
    // is required to override DynamicActionsMenu's link-required default;
    // it's disabled only when adding a schedule to a fully-paid invoice
    // (the backend rejects a $0 target anyway). Edit/View stay enabled.
    {
      key: InvoiceAction.EditPaymentSchedule,
      label: spbActive.value
        ? spbLocked.value || spbCancelled.value
          ? `View ${programName.value}`
          : `Edit ${programName.value}`
        : `Add ${programName.value}`,
      icon: "fa-regular fa-calendar-lines-pen",
      eventName: "edit-payment-schedule",
      className: "menu-item",
      visible:
        spbActive.value ||
        Boolean(props.tenantSpbEnabled && props.invoice?.job_id),
      disabled: !spbActive.value && fullyPaid.value,
      group: "payment"
    },
    {
      key: InvoiceAction.RemovePaymentSchedule,
      label: `Remove ${programName.value}`,
      icon: "fa-regular fa-calendar-xmark",
      eventName: "remove-payment-schedule",
      className: "menu-item",
      visible: spbActive.value && !spbLocked.value && !spbCancelled.value,
      disabled: false,
      group: "payment"
    },
    {
      key: InvoiceAction.Resubmit,
      label: "Resubmit for Payment",
      icon: "fa-regular fa-credit-card",
      eventName: "resubmit-invoice",
      className: "menu-item",
      link: links.resubmit,
      group: "payment"
    },
    {
      key: InvoiceAction.EditDetails,
      label: "Edit Details",
      icon: "fa-regular fa-calendar-lines-pen",
      eventName: "edit-details",
      className: "menu-item",
      link: links.update_details,
      group: "edit"
    },
    {
      key: InvoiceAction.EditDate,
      label: "Edit Date",
      icon: "fa-regular fa-calendar-lines-pen",
      eventName: "edit-date",
      className: "menu-item",
      link: links.edit_invoice_date,
      tooltip: !links.edit_invoice_date
        ? "Invoice must be approved before date can be edited."
        : "",
      group: "edit"
    },
    ...(inv.exclude_from_batching
      ? [
          {
            key: InvoiceAction.ResumeBatch,
            label: "Resume Batching",
            icon: "fa-regular fa-badge-check",
            eventName: "resume-batch",
            className: "menu-item",
            link: links.update_batching,
            group: "share"
          } as InvoiceMenuAction
        ]
      : [
          {
            key: InvoiceAction.DoNotBatch,
            label: "Do Not Batch",
            icon: "fa-regular fa-file-circle-xmark",
            eventName: "do-not-batch",
            className: "menu-item",
            link: links.update_batching,
            group: "share"
          } as InvoiceMenuAction
        ]),
    {
      key: InvoiceAction.Download,
      label: "Download Invoice",
      icon: "fa-regular fa-download",
      eventName: "download",
      className: "menu-item",
      link: links.invoice_pdf,
      group: "share"
    },
    {
      key: InvoiceAction.Email,
      label: "Email",
      icon: "fa-regular fa-envelope",
      eventName: "email-invoice",
      className: "menu-item",
      link: links.invoice_email,
      group: "share"
    }
  ];

  return base;
});

const actions = computed<InvoiceMenuAction[]>(() => {
  if (!props.visibleActions) return allActions.value;
  return allActions.value.filter((a) => props.visibleActions!.includes(a.key));
});

function handleAction(action: MenuAction) {
  const invoiceAction = action as InvoiceMenuAction;
  emit("invoice-action", {
    id: props.invoice.invoice_id,
    action: invoiceAction.key
  });
}
</script>

<style lang="scss" scoped>
.tooltip-trigger {
  text-align: center;
  cursor: pointer;
}
</style>
